import {
  faBlender,
  faBolt,
  faBroom,
  faBug,
  faCouch,
  faFire,
  faHammer,
  faHardHat,
  faKey,
  faLeaf,
  faPaintRoller,
  faShower,
  faSnowflake,
  faTint,
  faToolbox,
  faTruck,
  faTv,
  faWrench
} from '@fortawesome/free-solid-svg-icons'

export const PROVIDER_TYPES = {
  natural: 'natural',
  company: 'company'
}

export const PROVIDER_STATUS = {
  pending: { label: 'Pendiente de verificación', color: 'warning' },
  verified: { label: 'Verificado', color: 'success' },
  rejected: { label: 'Verificación rechazada', color: 'danger' },
  suspended: { label: 'Suspendido', color: 'danger' }
}

export const SERVICE_STATUS = {
  active: { label: 'Publicado', color: 'success' },
  paused: { label: 'Pausado', color: 'secondary' }
}

export const PRICING_MODES = [
  { value: 'fixed', label: 'Por servicio fijo', short: 'por servicio' },
  { value: 'hourly', label: 'Por hora', short: 'por hora' },
  { value: 'evaluation', label: 'Por visita de evaluación o diagnóstico', short: 'por evaluación' }
]

export const VISIT_FEE_TYPES = [
  { value: 'fixed', label: 'Tiene un costo fijo' },
  { value: 'free_if_hired', label: 'Gratis si se aprueba el presupuesto' }
]

export const WEEK_DAYS = [
  { value: 1, label: 'Lunes', short: 'Lun' },
  { value: 2, label: 'Martes', short: 'Mar' },
  { value: 3, label: 'Miércoles', short: 'Mié' },
  { value: 4, label: 'Jueves', short: 'Jue' },
  { value: 5, label: 'Viernes', short: 'Vie' },
  { value: 6, label: 'Sábado', short: 'Sáb' },
  { value: 7, label: 'Domingo', short: 'Dom' }
]

export const DOCUMENTS = {
  dni_front: {
    label: 'Cédula de identidad (frente)',
    help: 'Foto o escaneo legible del frente de tu cédula.'
  },
  dni_back: {
    label: 'Cédula de identidad (reverso)',
    help: 'Foto o escaneo legible del reverso de tu cédula.'
  },
  background_check: {
    label: 'Certificado de antecedentes o referencias personales',
    help: 'PDF o foto del certificado, o una carta de referencias con nombre y teléfono de quien la firma.'
  },
  rif: {
    label: 'RIF digitalizado',
    help: 'Copia vigente del RIF de la empresa.'
  },
  incorporation_deed: {
    label: 'Documento constitutivo',
    help: 'Opcional por ahora. Acta constitutiva registrada de la empresa.'
  }
}

export const DOCUMENTS_BY_TYPE = {
  natural: { required: ['dni_front', 'dni_back', 'background_check'], optional: [] },
  company: { required: ['rif'], optional: ['incorporation_deed'] }
}

export const DOCUMENT_ACCEPT = 'image/png, image/jpeg, image/gif, image/webp, application/pdf'
export const PHOTO_ACCEPT = 'image/png, image/jpeg, image/webp'
export const MAX_DOCUMENT_MB = 10
export const MAX_PHOTO_MB = 5
export const MIN_SERVICE_PHOTOS = 2
export const MAX_SERVICE_PHOTOS = 10
export const MAX_REQUEST_PHOTOS = 5

const CATEGORY_ICONS = {
  plomeria: faWrench,
  electricidad: faBolt,
  refrigeracion: faSnowflake,
  carpinteria: faHammer,
  albanileria: faHardHat,
  electrodomesticos: faBlender,
  remodelacion: faPaintRoller
}

export const CATEGORY_ICON_OPTIONS = {
  wrench: faWrench,
  bolt: faBolt,
  snowflake: faSnowflake,
  hammer: faHammer,
  'hard-hat': faHardHat,
  blender: faBlender,
  'paint-roller': faPaintRoller,
  toolbox: faToolbox,
  broom: faBroom,
  leaf: faLeaf,
  key: faKey,
  bug: faBug,
  fire: faFire,
  shower: faShower,
  couch: faCouch,
  tv: faTv,
  tint: faTint,
  truck: faTruck
}

export const categoryIcon = (category) =>
  category && typeof category === 'object'
    ? CATEGORY_ICON_OPTIONS[category.icon] || CATEGORY_ICONS[category.slug] || faToolbox
    : CATEGORY_ICONS[category] || faToolbox

export const storageUrl = (path) => {
  if (!path) return null
  if (/^https?:\/\//i.test(path)) return path
  return `${import.meta.env.VITE_URL}/storage/${path}`
}

const money = (value) =>
  `$${Number(value || 0).toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

export const formatMoney = money

export const formatPriceRange = (min, max) =>
  Number(min) === Number(max) ? money(min) : `${money(min)} - ${money(max)}`

export const formatVisitFee = (service) =>
  service.visit_fee_type === 'fixed' ? `Visita técnica: ${money(service.visit_fee)}` : 'Visita gratis si aceptas el presupuesto'

export const pricingModeShort = (mode) => PRICING_MODES.find((item) => item.value === mode)?.short || ''

export const formatAvailability = (days = [], from, to) => {
  const sorted = [...days].sort((a, b) => a - b)
  const isRange = sorted.length > 2 && sorted.every((day, index) => index === 0 || day === sorted[index - 1] + 1)
  const label = (day) => WEEK_DAYS.find((item) => item.value === day)?.short
  const daysText = isRange
    ? `${label(sorted[0])} a ${label(sorted[sorted.length - 1])}`
    : sorted.map(label).join(', ')
  return from && to ? `${daysText} · ${from} a ${to}` : daysText
}

export const isFileTooBig = (file, maxMb) => file.size > maxMb * 1024 * 1024

export const providerCardData = (provider) =>
  provider
    ? {
        name: provider.display_name,
        photoUrl: storageUrl(provider.photo),
        isCompany: provider.type === PROVIDER_TYPES.company,
        verified: provider.is_verified
      }
    : null

export const providerLocation = (provider) => [provider?.city, provider?.state].filter(Boolean).join(', ')

export const formatIdentification = (value, isRif = false) => {
  const match = /^([A-Z])(\d+)$/.exec(value || '')
  if (!match) return value
  const [, letter, digits] = match
  if (isRif && digits.length >= 9) return `${letter}-${digits.slice(0, -1)}-${digits.slice(-1)}`
  return `${letter}-${Number(digits).toLocaleString('es-VE')}`
}
